// author-liumingyang 2022-3-8  经分
import React, { useMemo, useState } from 'react';
import { getPercentage, getPercentageValue } from '@/pages/opinion/utils';
import EChartCommon from '@/pages/opinion/components/EChartCommon';
import { formatChartParamsDate } from '@/pages/opinion/components/ChartCommon/formatter';
import { reviewsFormat } from '@/pages/opinion/utils/value';
import { NO_DATE, baseColors, extendColors } from '@/pages/opinion/constants';
import { useIntl, getLocale } from 'umi';
import { getPositiveNegativeIconName } from '@op/pages/Performance/components/MentionsSentiment/chart';
import steamPositive from '@op/assets/svg/steam_positive.svg';
import steamNegative from '@op/assets/svg/steam_negative.svg';
import { DBO_V } from '@op/utils/format';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';

interface chartInterFace {
  data: any;
  height: number;
  transFunc: (name: string) => string;
  handleOnClick?: (value: any) => void;
  chartOptions?: any;
  loading?: boolean;
  rankBy?: string;
  setRankBy?: React.Dispatch<React.SetStateAction<string>>;
}

const colorArr = [...baseColors, ...extendColors];
// 这个图表暂时只有steam在用，后面可能要对数据处理进行处理
export default function StackBarChart(props: chartInterFace) {
  const {
    height,
    transFunc = (name: string) => name,
    loading,
    data: propData,
    handleOnClick,
    rankBy: rankByProps,
  } = props;
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const [rankBy, setRankBy] = useState(rankByProps || 'count');
  const isEn = getLocale() === 'en-US';
  // 设置图形配置

  const makeupOptions = () => {
    const data = Array.isArray(propData) ? propData : [];
    const positive: any = {
      name: 'Positive',
      type: 'bar',
      stack: 'total',
      itemStyle: { color: '#69D19C' },
      data: [],
    };
    const negative: any = {
      name: 'Negative',
      type: 'bar',
      stack: 'total',
      itemStyle: { color: '#F17C86' },
      data: [],
    };
    const total: any = {
      name: 'Total',
      type: 'bar',
      stack: 'Other',
      barGap: '-100%', // 左移100%，stack不再与需堆叠的柱子在一列
      label: {
        normal: {
          show: true,
          position: 'right', // 位置设为top
          formatter: (c: any) => {
            const { data } = c || {};
            return data?.percent;
          },
          textStyle: { color: '#444' },
        },
      },
      itemStyle: {
        normal: {
          color: 'rgba(128, 128, 128, 0)', // 为透明
        },
      },
      emphasis: {
        disabled: true,
      },
      data: [],
    };

    const yAxis: Array<string> = [];
    const rightYAxis: Array<number> = [];
    let totalAmount = 0;

    data
      .sort((a: { [key: string]: number }, b: { [key: string]: number }) => {
        if (rankBy === 'count') {
          return (a?.[rankBy] || 0) - (b?.[rankBy] || 0);
        }
        const aValue = a?.[rankBy] || 0,
          aTotal = a?.count || 0;
        const bValue = b?.[rankBy] || 0,
          bTotal = b?.count || 0;

        if (aTotal === 0 && bTotal === 0) {
          return 0;
        }

        if (aTotal === 0 && bTotal !== 0) {
          return -1;
        }

        if (aTotal !== 0 && bTotal === 0) {
          return 1;
        }

        try {
          const aPercent = DBO_V.percentageValue(aValue, aTotal);
          const bPercent = DBO_V.percentageValue(bValue, bTotal);
          return aPercent - bPercent;
        } catch (error) {
          return 0;
        }
      })
      .map(
        (item: {
          name: any;
          positive_mentions: any;
          negative_mentions: any;
        }) => {
          const totalValue = item.positive_mentions + item.negative_mentions;
          const positiveRate = DBO_V.percentage(
            item.positive_mentions,
            totalValue,
          );
          positive.data.push(item.positive_mentions);
          negative.data.push(item.negative_mentions);
          total.data.push({
            value: totalValue,
            percent: positiveRate,
          });
          yAxis.push(item.name);
          totalAmount += totalValue;
          rightYAxis.push(totalValue);
        },
      );
    const opts = {
      grid: {
        right: 0,
        containLabel: true,
      },
      legend: {
        data: [
          { name: 'Positive', itemStyle: { color: '#69D19C' } },
          { name: 'Negative', itemStyle: { color: '#F17C86' } },
        ],
        textStyle: {
          rich: {
            positive: {
              height: 18,
              width: 18,
              paddingRight: 4,
              backgroundColor: {
                image: steamPositive,
              },
            },
            negative: {
              height: 18,
              width: 18,
              paddingRight: 4,
              backgroundColor: {
                image: steamNegative,
              },
            },
          },
        },
        formatter: (name: string) => {
          const newName = formatMessage({ id: name || NO_DATE.EMPTY });
          if (name === 'Negative') {
            return `{negative|} ${newName}`;
          }
          return `{positive|} ${newName}`;
        },
      },
      color: colorArr,
      tooltip: {
        formatter: (params: any) =>
          formatChartParamsDate({
            title: transFunc(params[0].name),
            params: params,
            beforeSort: false,
            listTitleFunc: (params) => {
              const name = params.seriesName;
              return getPositiveNegativeIconName(name, formatMessage);
            },
            valueFormat: (value) => reviewsFormat(value),
            dealwithRecords(records) {
              const totalIndex = records.findIndex(
                (item) => item.originText === formatMessage({ id: 'Total' }),
              );
              const totalItem: any = records.splice(totalIndex, 1)?.[0] || {
                originValue: 0,
                value: 0,
              };
              const percent = (item: any) =>
                item?.originValue
                  ? `(${getPercentage(
                      item.originValue,
                      totalItem.originValue,
                    )})`
                  : '';
              const newRecords = records.map((item) => ({
                ...item,
                value: `${item.value}${percent(item)}`,
              }));
              newRecords.unshift({
                ...totalItem,
                text: formatMessage({ id: 'Mentions' }),
              });
              newRecords.push({
                text: `${formatMessage({
                  id: 'Click the chart to to view related keywords and reviews',
                })}`,
                value: '',
                originValue: 0,
                originText:
                  'Click the chart to to view related keywords and reviews',
              });
              return newRecords;
            },
          }),
      },
      yAxis: [
        {
          position: 'left',
          axisLabel: {
            show: true,
            margin: 24,
            interval: 0,
            align: 'right',
            formatter: (value: string) => transFunc(value),
          },
          splitLine: {
            show: false,
            lineStyle: { color: '#E5E5E5', type: 'dashed' },
          },
          axisLine: {
            show: false,
          },
          type: 'category',
          axisTick: {
            show: false,
          },
          gridIndex: 0,
          data: yAxis,
        },
        {
          position: 'right',
          axisLabel: {
            show: true,
            interval: 0,
            formatter: (value: number) => {
              const percent = DBO_V.percentage(value, totalAmount, 2);
              return `{value|${DBO_V.reviews(value)}(${percent})}`;
            },
            width: 120,
            rich: {
              value: {
                align: 'right',
              },
            },
            fontWeight: 400,
            fontSize: 16,
            lineHeight: 16,
            padding: [0, 0, 0, 0],
          },
          splitLine: {
            show: false,
            lineStyle: { color: '#E5E5E5', type: 'dashed' },
          },
          axisLine: {
            show: false,
          },
          type: 'category',
          axisTick: {
            show: false,
          },
          gridIndex: 0,
          data: rightYAxis,
        },
      ],
      xAxis: {
        axisTick: {
          alignWithLabel: true,
          lineStyle: {
            color: '#000000',
            opacity: '0.25',
          },
        },
        // max: maxXis + 0.2,
        boundaryGap: true,
        type: 'value',
        splitLine: {
          show: true,
          lineStyle: { color: '#E5E5E5', type: 'dashed' },
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#E5E5E5' },
        },
        axisLabel: {
          interval: 'auto',
          formatter: (value: number) => {
            return reviewsFormat(value, 0, '0');
          },
        },
      },
      series: [positive, negative, total],
    };
    return opts;
  };

  const options = useMemo(() => {
    if (propData.length === 0) {
      return {};
    }
    return makeupOptions();
  }, [propData, locale, rankBy]);

  return (
    <EChartCommon
      optionsProps={{ type: 'bar' }}
      options={options}
      loading={loading}
      empty={propData.length === 0}
      height={height}
      eventsDict={{
        click: (params: any) => {
          if (params.seriesName === 'Total') {
            return;
          }
          // console.log(" click: (params: any)", params);
          handleOnClick?.({
            ...params,
            rankBy,
            reviewsRating: params.seriesName.toLocaleLowerCase(),
          });
        },
      }}
      rightTitle={formatMessage({ id: 'Mentions%' })}
      leftTitle={
        <>
          <SortBySelect
            defaultValue={rankBy}
            onChange={(key: string, value: any) => {
              setRankBy(value);
            }}
            defaultParams={{
              options: [
                { value: 'count', text: 'Added Reviews' },
                { value: 'positive_mentions', text: 'Positive%' },
                { value: 'negative_mentions', text: 'Negative%' },
              ],
              btnTextPrefix: isEn ? formatMessage({ id: 'Ranked By' }) : '',
              matchButtonWidth: true,
              dealwithButton: (value: any, { textMap }: any) => {
                const valueText = formatMessage({ id: textMap[value] });
                return isEn
                  ? valueText
                  : formatMessage(
                      { id: 'Ranked By {value}' },
                      { value: valueText },
                    );
              },
            }}
          />
        </>
      }
    ></EChartCommon>
  );
}
